import { useMemo } from 'react';

import { Row, Table } from '@tanstack/react-table';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { parseFormNumber } from '@/helpers/finance';

import { getBillFieldName } from '../../components/generate-bills-table/bill-row.helpers';
import { GenerateBillListItem, GenerateBillSubRow, GenerateBillSubRowType } from '../../types';
import { calculateLineAmount } from '../../utils';
import { GenerateBillsTableFormValues } from '../../validations';

interface UseBillsGenerateDisabledArgs {
  form: UseFormReturn<GenerateBillsTableFormValues>;
  table: Table<GenerateBillListItem>;
  selectedRowsCount: number;
}

interface UseBillsGenerateDisabledResult {
  isGenerateDisabled: boolean;
  disabledReason: string | undefined;
}

type FormReader = UseFormReturn<GenerateBillsTableFormValues>['getValues'];

enum RowInvalidReason {
  ZERO_TOTALS = 'zeroTotals',
  EMPTY_OVERHEAD_NAME = 'emptyOverheadName',
  MISSING_PROJECT = 'missingProject',
  NEGATIVE_TOTAL = 'negativeTotalPerEntity',
}

// Field names are keyed by entity id, so a row reads its own leaf directly — no need to map
// its position among same-typed siblings back to an array index.
const readField = (getValues: FormReader, row: Row<GenerateBillListItem>, name: string) =>
  getValues(getBillFieldName(row, name) as never) as unknown;

const getRowInvalidReason = (
  rowObject: Row<GenerateBillListItem>,
  getValues: FormReader
): RowInvalidReason | null => {
  const subRow = rowObject.original as unknown as GenerateBillSubRow;

  if (
    subRow.type === GenerateBillSubRowType.ADD_OVERHEAD ||
    subRow.type === GenerateBillSubRowType.SALARY_ADJUSTMENT
  ) {
    return null;
  }

  if (subRow.type === GenerateBillSubRowType.OVERHEAD) {
    const description = String(
      readField(getValues, rowObject, 'description') ?? subRow.description
    );
    return description.trim() === '' ? RowInvalidReason.EMPTY_OVERHEAD_NAME : null;
  }

  if (subRow.type === GenerateBillSubRowType.ADDED_PROJECT) {
    const projectId = readField(getValues, rowObject, 'projectId') as number | null | undefined;
    if (projectId === null || projectId === undefined) return RowInvalidReason.MISSING_PROJECT;
    const hours = parseFormNumber(readField(getValues, rowObject, 'hours') ?? 0);
    const rate = parseFormNumber(readField(getValues, rowObject, 'effectiveRate') ?? 0);
    return hours === 0 && rate === 0 ? RowInvalidReason.ZERO_TOTALS : null;
  }

  // PROJECT / TIME_OFF
  const hours = parseFormNumber(
    readField(getValues, rowObject, 'hours') ?? rowObject.original.hours
  );
  const rate = parseFormNumber(
    readField(getValues, rowObject, 'effectiveRate') ?? rowObject.original.effectiveRate
  );
  return hours === 0 && rate === 0 ? RowInvalidReason.ZERO_TOTALS : null;
};

const getRowAmount = (rowObject: Row<GenerateBillListItem>, getValues: FormReader): number => {
  const subRow = rowObject.original as unknown as GenerateBillSubRow;
  if (subRow.type === GenerateBillSubRowType.OVERHEAD) {
    return parseFormNumber(readField(getValues, rowObject, 'effectiveRate') ?? 0);
  }
  if (
    subRow.type === GenerateBillSubRowType.PROJECT ||
    subRow.type === GenerateBillSubRowType.TIME_OFF ||
    subRow.type === GenerateBillSubRowType.ADDED_PROJECT
  ) {
    const hours = parseFormNumber(
      readField(getValues, rowObject, 'hours') ?? rowObject.original.hours ?? 0
    );
    const rate = parseFormNumber(
      readField(getValues, rowObject, 'effectiveRate') ?? rowObject.original.effectiveRate ?? 0
    );

    return calculateLineAmount({ hours, rate });
  }
  return 0;
};

const hasNegativeTotalPerMember = (
  selectedRowObjects: Row<GenerateBillListItem>[],
  getValues: FormReader
): boolean => {
  const totalsByParent = new Map<string, number>();
  const salaryMembers = new Map<string, Row<GenerateBillListItem>>();

  for (const rowObject of selectedRowObjects) {
    const parentRow = rowObject.getParentRow();
    if (!parentRow) continue;

    // Members billed as monthly salary are evaluated on their real bill total (salary +
    // bonuses/deductions) rather than the sum of their selected lines: the salary
    // adjustment is designed to go negative when the selected lines exceed the salary, so
    // summing the lines would flag a perfectly valid reconciliation.
    if (parentRow.original.isMonthlySalaryEnabled) {
      salaryMembers.set(parentRow.id, parentRow);
      continue;
    }

    totalsByParent.set(
      parentRow.id,
      (totalsByParent.get(parentRow.id) ?? 0) + getRowAmount(rowObject, getValues)
    );
  }

  for (const memberRow of salaryMembers.values()) {
    const overheadsTotal = memberRow.subRows.reduce((sum, subRow) => {
      const subRowType = (subRow.original as unknown as GenerateBillSubRow).type;
      if (subRowType !== GenerateBillSubRowType.OVERHEAD) return sum;
      if (!subRow.getIsSelected()) return sum;
      return sum + getRowAmount(subRow, getValues);
    }, 0);

    if (memberRow.original.monthlySalaryAmount + overheadsTotal < 0) return true;
  }

  for (const total of totalsByParent.values()) {
    if (total < 0) return true;
  }
  return false;
};

const REASON_TO_TOOLTIP: Record<RowInvalidReason, string> = {
  [RowInvalidReason.ZERO_TOTALS]: 'generateEntities.disabledTooltipZeroValues',
  [RowInvalidReason.EMPTY_OVERHEAD_NAME]: 'generateEntities.disabledTooltipEmptyOverheadName',
  [RowInvalidReason.MISSING_PROJECT]: 'generateEntities.disabledTooltipMissingProject',
  [RowInvalidReason.NEGATIVE_TOTAL]: 'generateEntities.disabledTooltipNegativeTotalBills',
};

export const useBillsGenerateDisabled = ({
  form,
  table,
  selectedRowsCount,
}: UseBillsGenerateDisabledArgs): UseBillsGenerateDisabledResult => {
  const { t: tCommon } = useTranslation('common');
  // Watch the whole form so this recomputes on any edit; leaves are read by id path.
  const watchedMember = useWatch({ control: form.control, name: 'member' });
  const selectedRowObjects = table.getSelectedRowModel().flatRows;

  // Members switched to monthly salary are billable on the strength of the salary alone —
  // their bill is a single salary line — so they count as "something to generate" even with
  // no sub-row selected.
  const salaryOnlyMembersCount = table
    .getCoreRowModel()
    .rows.filter(memberRow => memberRow.original.isMonthlySalaryEnabled).length;

  const hasBillableWork = selectedRowsCount > 0 || salaryOnlyMembersCount > 0;

  const invalidReason = useMemo<RowInvalidReason | null>(() => {
    if (!selectedRowsCount) return null;
    const { getValues } = form;
    for (const rowObject of selectedRowObjects) {
      const reason = getRowInvalidReason(rowObject, getValues);
      if (reason) return reason;
    }
    if (hasNegativeTotalPerMember(selectedRowObjects, getValues)) {
      return RowInvalidReason.NEGATIVE_TOTAL;
    }
    return null;
    // `watchedMember` drives recomputation; `form.getValues` reads the latest leaf values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowsCount, selectedRowObjects, watchedMember]);

  const isGenerateDisabled = !hasBillableWork || invalidReason !== null;

  const disabledReason = useMemo(() => {
    if (!hasBillableWork) return tCommon('generateEntities.disabledTooltipNoSelection');
    if (invalidReason) return tCommon(REASON_TO_TOOLTIP[invalidReason]);
    return undefined;
  }, [hasBillableWork, invalidReason, tCommon]);

  return { isGenerateDisabled, disabledReason };
};
